import { Directive, ElementRef, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Propriedades do `vivest-checkbox` (design system) usadas pela integração com formulários. */
type VivestCheckboxElement = HTMLElement & { checked: boolean; disabled: boolean };

/**
 * Liga o `<vivest-checkbox>` do design system aos formulários do Angular (`formControlName`,
 * `formControl` e `ngModel`). O web component expõe `checked`/`disabled` como propriedades e
 * dispara `change` ao ser marcado ou desmarcado, mas não implementa `ControlValueAccessor`.
 *
 * @example
 * <vivest-checkbox label-variant="multi-line" formControlName="aceito">Aceito os termos</vivest-checkbox>
 */
@Directive({
  selector:
    'vivest-checkbox[formControlName], vivest-checkbox[formControl], vivest-checkbox[ngModel]',
  standalone: true,
  host: {
    '(change)': 'aoAlterar()',
    '(focusout)': 'aoSair()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivestCheckboxValueAccessorDirective),
      multi: true,
    },
  ],
})
export class VivestCheckboxValueAccessorDirective implements ControlValueAccessor {
  private readonly elemento = inject<ElementRef<VivestCheckboxElement>>(ElementRef).nativeElement;

  private onChange: (valor: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(valor: boolean | null): void {
    this.elemento.checked = !!valor;
  }

  registerOnChange(fn: (valor: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this.elemento.disabled = desabilitado;
  }

  aoAlterar(): void {
    this.onChange(this.elemento.checked);
  }

  aoSair(): void {
    this.onTouched();
  }
}
